/**
 * Chart drawing for the HTML frame design runtime, as script source the
 * runtime embeds (`html-frame-design-runtime.ts`). A page asks for a chart
 * with attributes, never script:
 *
 *   <figure class="chart" data-chart="bar|line" data-labels="A|B|C"
 *     data-values="1,2,3" (line: series split by ";", data-series="X|Y")
 *     data-unit="%" data-highlight="B"><figcaption>…</figcaption></figure>
 *   <span data-chart="spark" data-values="3,5,4,8"></span>
 *   <div data-chart="number" data-value="222" data-previous="180"
 *     data-better="down"></div>
 *   <div data-chart="ring" data-value="2" data-max="5"><div class="ring-label"><b>2/5</b>won</div></div>
 *
 * Marks are inline SVG coloured by classes, so the poster capture paints
 * them like the page; text goes in through textContent only. Data that is
 * not numbers leaves the authored content as it is.
 */
export const CHART_RUNTIME = `
  var SVG_NS="http://www.w3.org/2000/svg";
  function node(tag,cls,text){var el=document.createElement(tag);if(cls)el.className=cls;if(text!=null)el.textContent=text;return el;}
  function svg(tag,attrs){var el=document.createElementNS(SVG_NS,tag);for(var k in attrs)el.setAttribute(k,String(attrs[k]));return el;}
  function round(v){return Math.round(v*100)/100;}
  function parts(value,sep){return String(value||"").split(sep).map(function(s){return s.trim();});}
  function readSeries(value){
    var out=parts(value,";").filter(Boolean).map(function(s){return parts(s,",").map(function(v){return v===""?NaN:Number(v);});});
    return out.length&&out.every(function(s){return s.every(isFinite);})?out:null;
  }
  var formats={};
  function fmt(v,unit,digits){
    var d=digits==null?2:digits;
    if(!formats[d]){
      try{formats[d]=new Intl.NumberFormat(document.documentElement.lang||undefined,{maximumFractionDigits:d});}
      catch(e){formats[d]=new Intl.NumberFormat(undefined,{maximumFractionDigits:d});}
    }
    return formats[d].format(v)+(unit||"");
  }
  function range(values,fromZero){
    var lo=Math.min.apply(null,values),hi=Math.max.apply(null,values);
    if(fromZero){lo=Math.min(0,lo);hi=Math.max(0,hi);}
    return{lo:lo,hi:hi===lo?lo+1:hi};
  }
  function points(values,r,height){
    return values.map(function(v,i){
      return[values.length===1?50:round(i/(values.length-1)*100),round(2+(1-(v-r.lo)/(r.hi-r.lo))*(height-4))];
    });
  }
  function path(pts){return pts.map(function(p,i){return(i?"L":"M")+p[0]+" "+p[1];}).join("");}
  function drawBar(el,body,values,labels,unit){
    var max=Math.max.apply(null,values.map(function(v){return Math.max(0,v);}))||1;
    var highlight=el.getAttribute("data-highlight");
    var rows=node("div","chart-bars");
    values.forEach(function(v,i){
      var row=node("div","chart-bar"+(highlight!==null&&labels[i]===highlight?" is-highlight":""));
      row.appendChild(node("span","chart-bar-label",labels[i]||""));
      var track=svg("svg",{"class":"chart-bar-track",viewBox:"0 0 100 8",preserveAspectRatio:"none","aria-hidden":"true"});
      track.appendChild(svg("rect",{"class":"chart-bar-fill",x:0,y:0,height:8,width:round(Math.max(0,v)/max*100)}));
      row.appendChild(track);
      row.appendChild(node("span","chart-bar-value",fmt(v,unit)));
      rows.appendChild(row);
    });
    body.appendChild(rows);
    return values.map(function(v,i){return(labels[i]||"")+" "+fmt(v,unit);}).join(", ");
  }
  function drawLine(el,body,data,labels,unit){
    var r=range([].concat.apply([],data),true);
    var names=parts(el.getAttribute("data-series"),"|");
    var plot=node("div","chart-plot"),axis=node("div","chart-y");
    axis.appendChild(node("span","",fmt(r.hi,unit)));
    axis.appendChild(node("span","",fmt(r.lo,unit)));
    var g=svg("svg",{"class":"chart-line",viewBox:"0 0 100 40",preserveAspectRatio:"none","aria-hidden":"true"});
    [2,20,38].forEach(function(y){g.appendChild(svg("line",{"class":"chart-grid",x1:0,x2:100,y1:y,y2:y}));});
    data.forEach(function(values,i){
      var pts=points(values,r,40);
      if(i===0&&data.length===1)g.appendChild(svg("path",{"class":"chart-area",d:path(pts)+"L"+pts[pts.length-1][0]+" 38L"+pts[0][0]+" 38Z"}));
      g.appendChild(svg("path",{"class":"chart-series chart-series-"+(i%3+1),d:path(pts)}));
    });
    plot.appendChild(axis);plot.appendChild(g);
    var count=data[0].length,x=node("div","chart-x");
    [0,Math.floor((count-1)/2),count-1].filter(function(v,i,a){return a.indexOf(v)===i;}).forEach(function(i){x.appendChild(node("span","",labels[i]||""));});
    plot.appendChild(x);
    body.appendChild(plot);
    var summary=data.map(function(values,i){
      return(names[i]?names[i]+": ":"")+(labels[0]||"")+" "+fmt(values[0],unit)+" to "+(labels[values.length-1]||"")+" "+fmt(values[values.length-1],unit);
    });
    if(data.length>1||names[0]){
      var legend=node("div","chart-legend");
      data.forEach(function(values,i){legend.appendChild(node("span","chart-key chart-key-"+(i%3+1),(names[i]?names[i]+" ":"")+fmt(values[values.length-1],unit)));});
      body.appendChild(legend);
    }
    return summary.join("; ");
  }
  function drawSpark(body,values){
    var g=svg("svg",{"class":"spark-line",viewBox:"0 0 100 24",preserveAspectRatio:"none","aria-hidden":"true"});
    g.appendChild(svg("path",{"class":"chart-series chart-series-1",d:path(points(values,range(values,false),24))}));
    body.appendChild(g);
    return fmt(values[0])+" to "+fmt(values[values.length-1]);
  }
  // Progress toward a goal: one segment per step up to 12 steps, else one arc.
  function drawRing(el,body){
    var given=(el.getAttribute("data-value")||"").trim(),value=Number(given),max=Number(el.getAttribute("data-max"));
    if(!given||!isFinite(value)||!isFinite(max)||max<=0)return null;
    value=Math.max(0,Math.min(value,max));
    var g=svg("svg",{"class":"chart-ring",viewBox:"0 0 100 100","aria-hidden":"true"});
    function arc(start,length,on){
      g.appendChild(svg("circle",{"class":"chart-ring-seg"+(on?" is-on":""),cx:50,cy:50,r:42,pathLength:100,"stroke-dasharray":round(length)+" "+round(100-length),"stroke-dashoffset":round(25-start)}));
    }
    if(max<=12&&max===Math.round(max)){
      var step=100/max;
      for(var i=0;i<max;i++)arc(i*step+1.5,step-3,i+1<=value);
    }else{
      var done=value/max*100;
      arc(done,100-done,false);
      arc(0,done,true);
    }
    body.appendChild(g);
    return fmt(value)+" of "+fmt(max);
  }
  function drawNumber(el,body,unit){
    var given=(el.getAttribute("data-value")||"").trim(),value=Number(given),raw=el.getAttribute("data-previous");
    if(!given||!isFinite(value))return null;
    body.className+=" chart-number";
    body.appendChild(node("span","chart-number-value",fmt(value,unit)));
    if(raw!==null&&raw.trim()!==""&&isFinite(Number(raw))){
      var previous=Number(raw),diff=value-previous,better=el.getAttribute("data-better")==="down"?-1:1;
      var delta=node("span","delta "+(diff===0?"flat":diff*better>0?"positive":"negative"));
      if(diff!==0)delta.appendChild(node("span","",diff>0?"\\u25B2":"\\u25BC")).setAttribute("aria-hidden","true");
      delta.appendChild(document.createTextNode((diff!==0?" ":"")+(previous!==0?fmt(Math.abs(diff/previous*100),"%",1):fmt(Math.abs(diff),unit))));
      body.appendChild(delta);
    }
    return "";
  }
  function renderChart(el){
    var kind=el.getAttribute("data-chart"),unit=el.getAttribute("data-unit")||"";
    var body=node(kind==="spark"||el.tagName==="SPAN"?"span":"div","chart-body");
    var label=null;
    if(kind==="number")label=drawNumber(el,body,unit);
    else if(kind==="ring")label=drawRing(el,body);
    else{
      var data=readSeries(el.getAttribute("data-values")),labels=parts(el.getAttribute("data-labels"),"|");
      if(!data)return;
      if(kind==="bar")label=drawBar(el,body,data[0],labels,unit);
      else if(kind==="line")label=drawLine(el,body,data,labels,unit);
      else if(kind==="spark")label=drawSpark(body,data[0]);
    }
    if(label===null)return;
    if(label){body.setAttribute("role","img");body.setAttribute("aria-label",label);}
    body.setAttribute("data-chart-body","");
    for(var c=el.firstElementChild;c;c=c.nextElementSibling)if(c.hasAttribute("data-chart-body")){c.remove();break;}
    var first=el.firstElementChild;
    if(first&&first.tagName==="FIGCAPTION")el.appendChild(body);
    else el.insertBefore(body,el.firstChild);
  }
  // One malformed chart leaves only itself as authored.
  function renderCharts(){document.querySelectorAll("[data-chart]").forEach(function(el){try{renderChart(el);}catch(e){}});}
`;
